import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import {
  FiChevronDown,
  FiChevronUp,
  FiFilter,
  FiSearch,
  FiAlertTriangle,
} from "react-icons/fi";
import FilterPanel from "../FilterPanel";
import { useLocation } from "react-router-dom";
import NavegacaoEtapa from "../NavegacaoEtapa";
import { indiceDaRota } from "../NavegacaoEtapa/etapas";
import { usePrecosVisiveis } from "../../context/PrecosContext";
import { useThemeToggle } from "../../context/ThemeContext";
import { useDados } from "../../context/DadosContext";
import { normalizeText as normalizarPlanilha } from "../../utils/data";
import { F } from "../../utils/gerencialFields";
import AmbienteDetalhesDrawer from "../AmbienteDetalhesDrawer";
import { BalaoComentarios } from "../HistoricoAmbiente";
import { useExportRender } from "../../context/ExportRenderContext";
import { useAuth } from "../../context/AuthContext";
import useMediaQuery from "../../hooks/useMediaQuery";
import CardListView from "./CardListView";
import { mq } from "../../styles/canonical/breakpoints";
import { useExportSnapshot } from "../../hooks/useExportSnapshot";
import { formatarData, formatarMoeda, parseNumero } from "../../utils/calculos";
import { compareDatesAsc, inferStatusVariant, toDate } from "../../utils/status";
import BotaoExportacao from "../Exportacao";
import {
  ColumnResizeProvider,
  ResetColumnsButton,
  ResizableColgroup,
  ResizableTh,
} from "../ResizableColumns";
import {
  Wrapper,
  TopBar,
  Titulo,
  TopBarActions,
  SoDesktop,
  ExportCompacto,
  BuscaInput,
  SearchBox,
  FiltroSelect,
  FilterField,
  FilterGrid,
  FilterInlineGroup,
  FilterLabel,
  Totalizadores,
  TotalCard,
  TotalTopo,
  TotalLabel,
  TotalValor,
  TotalBar,
  TotalBarOk,
  TotalBarVenc,
  TotalLegend,
  TotalSub,
  TabelaWrapper,
  Tabela,
  Th,
  ThInner,
  ThIconHint,
  ThFunil,
  SoVencidosBtn,
  FunilPopover,
  FunilHead,
  FunilLimpar,
  FunilBusca,
  FunilLista,
  FunilItem,
  FunilVazio,
  TrLinha,
  Td,
  StatusBadge,
  EmptyTd,
  Paginacao,
  PagInfo,
  PagBotoes,
  PagBtn,
  PagEllipsis,
} from "./styles";

const ITENS_POR_PAGINA = 20;
// Acima deste tamanho a entrada/saida das linhas nao anima: o stagger de
// `index * 0.012` em centenas de motion.tr custava ~5s de animacao continua
// na main thread (telas sem paginacao renderizam as 400+ linhas de uma vez)
// e era um dos responsaveis pelos congelamentos de navegacao no legado.
const MAX_LINHAS_ANIMADAS = 60;
const EMPTY_VALUE = "\u2014";
const INITIAL_FILTERS = {
  busca: "",
  status: "TODOS",
  cliente: "TODOS",
  ambiente: "",
  lote: "",
  responsavel: "TODOS",
  campoData: "TODOS",
  dataDe: "",
  dataAte: "",
  valorMin: "",
  valorMax: "",
};

function sortRows(rows, columns, sorting) {
  if (!sorting.column) {
    return rows;
  }

  const selectedColumn = columns.find((column) => column.key === sorting.column);
  const direction = sorting.direction === "asc" ? 1 : -1;

  return [...rows].sort((left, right) => {
    const leftValue = left[sorting.column];
    const rightValue = right[sorting.column];

    if (selectedColumn?.tipo === "data") {
      return compareDatesAsc(leftValue, rightValue) * direction;
    }

    if (selectedColumn?.tipo === "moeda") {
      return ((parseNumero(leftValue) || 0) - (parseNumero(rightValue) || 0)) * direction;
    }

    return (
      String(leftValue ?? "").localeCompare(String(rightValue ?? ""), "pt-BR", {
        numeric: true,
        sensitivity: "base",
      }) * direction
    );
  });
}

function normalizeText(value) {
  return String(value ?? "").toLowerCase();
}

function hasMeaningfulValue(value) {
  return value != null && String(value).trim() !== "" && String(value).trim() !== EMPTY_VALUE;
}

function getUniqueOptions(rows, key) {
  return [...new Set(rows.map((row) => row[key]).filter(hasMeaningfulValue).map((value) => String(value).trim()))]
    .sort((left, right) =>
      String(left).localeCompare(String(right), "pt-BR", { numeric: true, sensitivity: "base" })
    );
}

function getNumberFilterValue(value) {
  if (value === "" || value == null) {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function matchesRange(value, min, max) {
  const minValue = getNumberFilterValue(min);
  const maxValue = getNumberFilterValue(max);

  if (minValue !== null && value < minValue) {
    return false;
  }

  if (maxValue !== null && value > maxValue) {
    return false;
  }

  return true;
}

function matchesDateRange(value, from, to) {
  const date = toDate(value);
  const fromDate = toDate(from);
  const toDateValue = toDate(to);

  if (!date) {
    return false;
  }

  if (fromDate && date < fromDate) {
    return false;
  }

  if (toDateValue && date > toDateValue) {
    return false;
  }

  return true;
}

function getFieldKey(rows, keys) {
  return keys.find((key) => rows.some((row) => hasMeaningfulValue(row[key]))) || null;
}

function getActiveChips(filters, meta) {
  const chips = [];

  if (filters.busca.trim()) {
    chips.push(`Busca: ${filters.busca.trim()}`);
  }

  if (filters.status !== "TODOS") {
    chips.push(`Status: ${filters.status}`);
  }

  if (filters.cliente !== "TODOS") {
    chips.push(`Cliente: ${filters.cliente}`);
  }

  if (filters.ambiente.trim()) {
    chips.push(`Ambiente: ${filters.ambiente.trim()}`);
  }

  if (filters.lote.trim()) {
    chips.push(`${meta.loteLabel}: ${filters.lote.trim()}`);
  }

  if (filters.responsavel !== "TODOS") {
    chips.push(`${meta.responsavelLabel}: ${filters.responsavel}`);
  }

  if (filters.campoData !== "TODOS" && (filters.dataDe || filters.dataAte)) {
    chips.push(
      `${meta.dateLabels[filters.campoData] || filters.campoData}: ${filters.dataDe || "inicio"} a ${
        filters.dataAte || "fim"
      }`
    );
  }

  if (filters.valorMin || filters.valorMax) {
    chips.push(`Valor: ${filters.valorMin || "0"} a ${filters.valorMax || "sem limite"}`);
  }

  return chips;
}

/**
 * A linha crua da planilha por trás da linha derivada da tabela.
 * `rowPlanilha` quando a página manda; senão cliente+ambiente normalizados,
 * e só quando o par aponta UMA linha (sem adivinhar entre pedidos).
 */
export function linhaCrua(row, dadosCrus) {
  if (row?.rowPlanilha) return row.rowPlanilha;
  if (!row?.cliente || !row?.ambiente || !Array.isArray(dadosCrus)) return null;
  const cliente = normalizarPlanilha(row.cliente);
  const ambiente = normalizarPlanilha(row.ambiente);
  const achadas = dadosCrus.filter(
    (r) => normalizarPlanilha(r[F.NOME_DO_CLIENTE]) === cliente && normalizarPlanilha(r[F.AMBIENTE]) === ambiente
  );
  return achadas.length === 1 ? achadas[0] : null;
}

export default function PainelTabela({
  titulo,
  linhas = [],
  colunas = [],
  totalizadores,
  conteudoAcimaTotalizadores,
  filtrosExtras,
  labelContagem = "Total de ambientes",
  densidade = "default",
  mostrarBusca = true,
  mostrarFiltroStatus = true,
  buscaPlaceholder = "Buscar...",
  mostrarPainelFiltros = true,
  // ID estavel usado pelo agendador de exportacao (PAINEIS_DISPONIVEIS).
  // Se omitido, cai em um slug do titulo - valido so quando o titulo ja
  // e um slug-friendly (ex: "Clientes"). Para titulos com parenteses,
  // barras ou acentos, passe explicitamente.
  exportPainelId,
  // Quando false, renderiza TODAS as linhas filtradas sem paginar.
  // Util quando o scroll da pagina inteira ja basta (cabecalho fixo no
  // topo da viewport via position: sticky).
  mostrarPaginacao = true,
  // Bloco TBL.2 (2026-05-25): quando true, larguras de colunas persistem
  // no backend (cross-device) via PreferenciasTabelasContext. Quando
  // false (default), comportamento legado: localStorage por dispositivo.
  // Tabelas migradas no Bloco TBL.5 passam true.
  persistirNoBackend = false,
  // Callback opcional ao clicar numa linha (recebe a linha mapeada). Quando
  // definido, as linhas viram clicaveis (cursor pointer). Retrocompativel:
  // paginas que nao passam mantem o comportamento legado (sem clique).
  onRowClick,
  // Visual e filtros por coluna (Excel) das telas do fluxo legado fora das
  // 15 rotas do fluxo — ex.: a pagina do setor do Mapa da Producao, que e
  // planilha lida com as mesmas regras. Default: so pela rota (legado).
  visualPlanilha = false,
  // false quando a pagina ja tem a propria faixa de KPIs (evita os cards
  // "Total / Em atraso / Valor total" repetidos acima da tabela).
  mostrarTotalizadores = true,
}) {
  const { precosVisiveis } = usePrecosVisiveis();
  const { renderAll } = useExportRender();
  // Bloco TBL.3 (2026-05-25): gate RBAC por coluna. Colunas com
  // `requerPermissao: "preco:view"` (ou outra permissao) somem por
  // completo (header, body, totalizadores, export) para usuarios que
  // nao tem. Master tem bypass automatico (hasPermission cobre).
  const { hasPermission } = useAuth();
  // Re-skin do Gerenciador de Produção (2026-07) é escopado às 15 telas do
  // fluxo legado. Detecta pela rota (mesma fonte do SeletorEtapas), então
  // outras páginas que reusam PainelTabela (Clientes, Orçamentos…) ficam
  // com o visual original.
  const location = useLocation();
  const naFluxo = visualPlanilha || indiceDaRota(location.pathname) >= 0;
  // Densidade global (Configurações → Tema & Cores): compact | cozy | comfort.
  // No fluxo, a tabela SEGUE essa configuração (25 / 37 / 50 px por linha),
  // ignorando o `densidade="compact"` que as páginas passam por retrocompat.
  const densidadeGlobal = useThemeToggle()?.densidade || "cozy";
  // Redesenho mobile do legado (2026-09-24): abaixo de 1024px (celular e
  // tablet) a lista é SEMPRE em cards compactos — sem tabela com scroll
  // lateral (regra constitucional) e sem o FAB que alternava os dois e
  // cobria o último item. Em modo export (renderAll) continua tabela.
  const compacto = useMediaQuery(mq.tablet);
  const usarCards = compacto && !renderAll;
  const wrapperRef = useRef(null);
  // Densidade tambem em modo de captura/exportacao (renderAll): a imagem
  // exportada sai IGUAL ao que o usuario ve na tela (pedido 2026-07-06 —
  // antes forcavamos compact para reduzir a altura, mas a exportacao deve
  // acompanhar o tema/densidade das Configuracoes).
  const densidadeEfetiva = naFluxo ? densidadeGlobal : densidade;

  const [filtros, setFiltros] = useState(() => ({ ...INITIAL_FILTERS }));
  const [ordenacao, setOrdenacao] = useState({ column: null, direction: "asc" });
  const [pagina, setPagina] = useState(1);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);

  // Drawer de detalhe do ambiente (fluxo): clique na linha abre o
  // AmbienteDetalhesDrawer (dados + linha do tempo + histórico). A linha da
  // tabela é um objeto DERIVADO; a row CRUA da planilha vem em `rowPlanilha`
  // (as páginas do fluxo mandam). Sem ela, relocaliza por cliente+ambiente
  // normalizados e SÓ quando o par é único — antes pegava a primeira que
  // batesse e podia abrir o ambiente de outro pedido (dois "COZINHA").
  const { dados: dadosCrus } = useDados() || {};
  const [ambienteDrawer, setAmbienteDrawer] = useState(null);
  function abrirDrawerAmbiente(row) {
    const raw = linhaCrua(row, dadosCrus);
    if (!raw) return;
    setAmbienteDrawer({
      ambiente: raw[F.AMBIENTE],
      lote: raw[F.NUMERO_DO_LOTE] ?? "—",
      row: raw,
    });
  }
  // Página pode passar onRowClick próprio (tem precedência); no fluxo o
  // default é abrir o drawer do ambiente.
  const rowClickEfetivo = onRowClick || (naFluxo ? abrirDrawerAmbiente : undefined);
  // Filtros estilo Excel por coluna (fluxo): { [colKey]: valoresPermitidos[] }.
  // Ausência da chave = todos permitidos; array vazio = nenhum.
  const [colFiltros, setColFiltros] = useState({});
  // Popover de funil aberto: { col, top, left } (coords do viewport) | null.
  const [funil, setFunil] = useState(null);
  const [funilBusca, setFunilBusca] = useState("");
  // Atalho "Só vencidos" (fluxo) — substitui o painel de Filtros do mockup.
  const [soVencidos, setSoVencidos] = useState(false);

  // Fecha o popover de funil ao clicar fora ou apertar Esc.
  useEffect(() => {
    if (!funil) return undefined;
    const onDown = (event) => {
      if (
        !event.target.closest("[data-funil-popover]") &&
        !event.target.closest("[data-funil-btn]")
      ) {
        setFunil(null);
      }
    };
    const onKey = (event) => {
      if (event.key === "Escape") setFunil(null);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [funil]);

  const statusOptions = useMemo(
    () => ["TODOS", ...new Set(linhas.map((item) => item.status).filter(Boolean))],
    [linhas]
  );

  const clientKey = useMemo(() => getFieldKey(linhas, ["cliente", "clienteResumo", "clientePrincipal"]), [linhas]);
  const ambienteKey = useMemo(() => getFieldKey(linhas, ["ambiente"]), [linhas]);
  const loteKey = useMemo(() => getFieldKey(linhas, ["numeroLote", "lote", "loteLabel"]), [linhas]);
  const responsavelKey = useMemo(
    () => getFieldKey(linhas, ["marceneiro", "vendedor", "consultor", "vendedoresLabel"]),
    [linhas]
  );
  // Bloco TBL.3: filtra colunas por permissao RBAC ANTES de qualquer
  // processamento. Coluna sem permissao some por completo: header, body,
  // totalizadores, export, filtros. Tabelas que querem gate de valor
  // passam `requerPermissao: "preco:view"` na definicao da coluna.
  //
  // Bugfix 2026-05-28 (#5): paginas legadas frequentemente esqueciam
  // de declarar `requerPermissao` em colunas de moeda — a coluna ficava
  // visivel com "••••••" mascarado, ocupando espaco util da tela.
  // Agora INFERIMOS automaticamente: toda coluna `tipo: "moeda"` exige
  // "preco:view" por default. Quem quer override (ex.: mostrar coluna
  // para todos) pode passar `requerPermissao: null` explicitamente.
  const colunasVisiveis = useMemo(
    () =>
      colunas.filter((c) => {
        const requerPermissao = c.requerPermissao !== undefined
          ? c.requerPermissao
          : (c.tipo === "moeda" ? "preco:view" : null);
        if (!requerPermissao) return true;
        return hasPermission(requerPermissao);
      }),
    [colunas, hasPermission]
  );

  const dateColumns = useMemo(() => colunasVisiveis.filter((column) => column.tipo === "data"), [colunasVisiveis]);
  const moedaColumn = useMemo(() => colunasVisiveis.find((column) => column.tipo === "moeda"), [colunasVisiveis]);

  const clientOptions = useMemo(
    () => (clientKey ? ["TODOS", ...getUniqueOptions(linhas, clientKey)] : []),
    [clientKey, linhas]
  );
  const responsavelOptions = useMemo(
    () => (responsavelKey ? ["TODOS", ...getUniqueOptions(linhas, responsavelKey)] : []),
    [linhas, responsavelKey]
  );

  const filterMeta = useMemo(
    () => ({
      loteLabel:
        loteKey === "loteLabel" ? "Lote" : loteKey === "numeroLote" ? "Número do lote" : "Lote",
      responsavelLabel:
        responsavelKey === "marceneiro"
          ? "Marceneiro"
          : responsavelKey === "consultor"
            ? "Consultor"
            : "Responsável",
      dateLabels: Object.fromEntries(dateColumns.map((column) => [column.key, column.label])),
    }),
    [dateColumns, loteKey, responsavelKey]
  );

  const dadosFiltrados = useMemo(() => {
    const filteredRows = linhas.filter((row) => {
      if (mostrarFiltroStatus && filtros.status !== "TODOS" && row.status !== filtros.status) {
        return false;
      }

      if (mostrarBusca && filtros.busca.trim()) {
        const query = filtros.busca.trim().toLowerCase();
        const searchMatches = Object.values(row).some((value) =>
          String(value ?? "").toLowerCase().includes(query)
        );

        if (!searchMatches) {
          return false;
        }
      }

      if (clientKey && filtros.cliente !== "TODOS" && String(row[clientKey] ?? "").trim() !== filtros.cliente) {
        return false;
      }

      if (ambienteKey && filtros.ambiente.trim()) {
        const ambienteValue = normalizeText(row[ambienteKey]);
        if (!ambienteValue.includes(filtros.ambiente.trim().toLowerCase())) {
          return false;
        }
      }

      if (loteKey && filtros.lote.trim()) {
        const loteValue = normalizeText(row[loteKey]);
        if (!loteValue.includes(filtros.lote.trim().toLowerCase())) {
          return false;
        }
      }

      if (
        responsavelKey &&
        filtros.responsavel !== "TODOS" &&
        String(row[responsavelKey] ?? "").trim() !== filtros.responsavel
      ) {
        return false;
      }

      if (moedaColumn) {
        const valor = parseNumero(row[moedaColumn.key]) || 0;
        if (!matchesRange(valor, filtros.valorMin, filtros.valorMax)) {
          return false;
        }
      }

      if (dateColumns.length && (filtros.dataDe || filtros.dataAte)) {
        if (filtros.campoData === "TODOS") {
          const hasMatchingDate = dateColumns.some((column) =>
            matchesDateRange(row[column.key], filtros.dataDe, filtros.dataAte)
          );

          if (!hasMatchingDate) {
            return false;
          }
        } else if (!matchesDateRange(row[filtros.campoData], filtros.dataDe, filtros.dataAte)) {
          return false;
        }
      }

      // Atalho "Só vencidos" (fluxo).
      if (soVencidos) {
        const variant = row.statusVariant ?? inferStatusVariant(row.status);
        if (variant !== "danger") return false;
      }

      // Filtros estilo Excel por coluna (fluxo).
      for (const colKey of Object.keys(colFiltros)) {
        const permitidos = colFiltros[colKey];
        const column = colunasVisiveis.find((c) => c.key === colKey);
        if (!column) continue;
        const v =
          column.key === "status"
            ? row.status ?? EMPTY_VALUE
            : row[colKey] === null || row[colKey] === undefined || row[colKey] === ""
              ? EMPTY_VALUE
              : String(row[colKey]);
        if (!permitidos.includes(v)) return false;
      }

      return true;
    });

    return sortRows(filteredRows, colunasVisiveis, ordenacao);
  }, [ambienteKey, clientKey, colFiltros, colunasVisiveis, dateColumns, filtros, linhas, loteKey, moedaColumn, mostrarBusca, mostrarFiltroStatus, ordenacao, responsavelKey, soVencidos]);

  const totalPaginas = Math.max(1, Math.ceil(dadosFiltrados.length / ITENS_POR_PAGINA));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const inicio = (paginaAtual - 1) * ITENS_POR_PAGINA;
  // Durante uma exportação visual (captureWithExpand), o ExportRenderContext
  // ativa `renderAll` e o componente renderiza TODAS as linhas filtradas,
  // ignorando a paginação UI. Após a captura, volta ao normal.
  // Quando `mostrarPaginacao={false}` também renderizamos tudo (cliente
  // confia no scroll natural da pagina + cabecalho sticky).
  const dadosPagina = renderAll || !mostrarPaginacao
    ? dadosFiltrados
    : dadosFiltrados.slice(inicio, inicio + ITENS_POR_PAGINA);

  const totalAtraso = dadosFiltrados.filter(
    (row) => (row.statusVariant ?? inferStatusVariant(row.status)) === "danger"
  ).length;

  const totalValor = dadosFiltrados.reduce(
    (accumulator, row) =>
      accumulator +
      (moedaColumn ? parseNumero(row[moedaColumn.key]) || 0 : parseNumero(row.valor) || 0),
    0
  );

  const activeChips = useMemo(() => getActiveChips(filtros, filterMeta), [filterMeta, filtros]);

  function handleOrdenar(column) {
    setPagina(1);
    setOrdenacao((previous) =>
      previous.column === column
        ? {
            column,
            direction: previous.direction === "asc" ? "desc" : "asc",
          }
        : { column, direction: "asc" }
    );
  }

  function handleFiltroChange(key, value) {
    setPagina(1);
    setFiltros((current) => ({
      ...current,
      [key]: value,
    }));
  }

  function resetFiltros() {
    setPagina(1);
    setFiltros({ ...INITIAL_FILTERS });
    setColFiltros({});
    setSoVencidos(false);
    setFunil(null);
  }

  // ----- Filtro estilo Excel por coluna (fluxo) -----
  // Valor "exibido" de uma célula (o que o usuário vê e por onde filtra).
  function valorExibido(row, column) {
    if (column.key === "status") return row.status ?? EMPTY_VALUE;
    const raw = row[column.key];
    return raw === null || raw === undefined || raw === "" ? EMPTY_VALUE : String(raw);
  }
  // Valores distintos de uma coluna, na ordem de aparição.
  function distinctValores(colKey) {
    const column = colunasVisiveis.find((c) => c.key === colKey);
    if (!column) return [];
    const vistos = new Set();
    const out = [];
    linhas.forEach((row) => {
      const v = valorExibido(row, column);
      if (!vistos.has(v)) {
        vistos.add(v);
        out.push(v);
      }
    });
    return out;
  }
  function abrirFunil(colKey, event) {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    let left = rect.left;
    if (left > window.innerWidth - 280) left = window.innerWidth - 282;
    setFunil((atual) =>
      atual && atual.col === colKey ? null : { col: colKey, top: rect.bottom + 6, left }
    );
    setFunilBusca("");
  }
  function toggleFunilVal(colKey, valor) {
    setPagina(1);
    setColFiltros((prev) => {
      const cf = { ...prev };
      const todos = distinctValores(colKey);
      const set = cf[colKey] ? cf[colKey].slice() : todos.slice();
      const i = set.indexOf(valor);
      if (i >= 0) set.splice(i, 1);
      else set.push(valor);
      if (set.length === 0) cf[colKey] = [];
      else if (set.length === todos.length) delete cf[colKey];
      else cf[colKey] = set;
      return cf;
    });
  }
  function toggleTodosFunil(colKey) {
    setPagina(1);
    setColFiltros((prev) => {
      const cf = { ...prev };
      if (cf[colKey] === undefined) cf[colKey] = [];
      else delete cf[colKey];
      return cf;
    });
  }
  function limparColFiltro(colKey) {
    setColFiltros((prev) => {
      const cf = { ...prev };
      delete cf[colKey];
      return cf;
    });
  }

  // `densidade` opcional: os cards do celular pedem "cozy" para o selo de
  // status não cair abaixo do piso de 12px (compact = 10px).
  function renderCellValue(row, column, densidade = densidadeEfetiva) {
    if (column.render) {
      return column.render({ row, precosVisiveis });
    }

    // Balão de comentários do histórico do ambiente — fora da exportação.
    if (naFluxo && !renderAll && column.key === "ambiente" && row.rowPlanilha) {
      return (
        <>
          {row[column.key] ?? EMPTY_VALUE}
          <BalaoComentarios row={row.rowPlanilha} />
        </>
      );
    }

    if (column.key === "status") {
      return (
        <StatusBadge
          $density={densidade}
          $fluxo={naFluxo}
          $tone={row.statusVariant ?? inferStatusVariant(row.status)}
        >
          {row.status ?? EMPTY_VALUE}
        </StatusBadge>
      );
    }

    const value = row[column.key];

    if (column.tipo === "data") {
      return formatarData(value);
    }

    if (column.tipo === "moeda") {
      return precosVisiveis ? formatarMoeda(value) : "••••••";
    }
    // (texto cru cai no return abaixo do bloco original — sem mudanca)

    return value ?? EMPTY_VALUE;
  }

  /**
   * Bloco TBL.3 (2026-05-25) - tooltip nativo via atributo `title`:
   * quando a cell tem texto longo que pode ser cortado pelo
   * `text-overflow: ellipsis`, o `title` mostra o conteudo completo ao
   * hover do mouse. Funciona zero-JS, zero-dependencia. Limitacoes
   * (delay ~500ms do SO, estilo nativo, sem touch) sao aceitas pra MVP.
   *
   * Skipa colunas com render custom (delegamos a elas) e colunas que sao
   * status/badges (texto curto, ja cabe sempre).
   */
  function tituloCelula(row, column) {
    if (column.render) return undefined;
    if (column.key === "status") return undefined;
    const value = row[column.key];
    if (column.tipo === "data") return formatarData(value) || undefined;
    if (column.tipo === "moeda") {
      return precosVisiveis ? formatarMoeda(value) : undefined;
    }
    if (value == null || value === "") return undefined;
    const s = String(value);
    return s.length > 0 ? s : undefined;
  }

  // Exportação: monta `resumo` + `secoes` a partir das colunas e dados
  // já filtrados. A exportação inclui SEMPRE todas as linhas filtradas
  // (não só a página atual). PainelTabela respeita o ExportRenderContext
  // para também não paginar na captura visual de PNG/JPG.
  const exportResumo = useMemo(() => {
    const base = [
      { label: labelContagem, value: dadosFiltrados.length, type: "number" },
      { label: "Em atraso", value: totalAtraso, type: "number" },
      ...(moedaColumn
        ? [{ label: "Valor total", value: totalValor, type: "currency" }]
        : []),
      ...(totalizadores || []).map((t) => ({
        label: t.label,
        value: t.valor,
        type: "text",
      })),
    ];
    // Dedup por label normalizado: os totalizadores da página costumam
    // repetir os KPIs base com outra capitalização ("Total de Ambientes" vs
    // "Total de ambientes") — no arquivo isso aparecia como indicador dobrado.
    const vistos = new Set();
    return base.filter((item) => {
      const chave = String(item.label || "").trim().toLowerCase();
      if (vistos.has(chave)) return false;
      vistos.add(chave);
      return true;
    });
  }, [dadosFiltrados.length, labelContagem, moedaColumn, totalAtraso, totalValor, totalizadores]);

  const exportColumns = useMemo(
    () =>
      colunasVisiveis.map((c) => ({
        key: c.key,
        label: c.label,
        type:
          c.tipo === "moeda"
            ? "currency"
            : c.tipo === "data"
              ? "date"
              : c.key === "status"
                ? "status"
                : "text",
      })),
    [colunasVisiveis]
  );

  const exportSecoes = useMemo(
    () => [
      {
        id: "tabela",
        label: titulo || "Detalhe",
        columns: exportColumns,
        rows: dadosFiltrados,
      },
    ],
    [titulo, exportColumns, dadosFiltrados]
  );

  const exportVisual = useMemo(
    () => ({
      ref: wrapperRef,
      sections: [
        { id: "totalizadores", label: "Cards de resumo", selector: "[data-pdf='totalizadores']" },
        { id: "tabela", label: "Tabela", selector: "[data-pdf='tabela']" },
      ],
    }),
    []
  );

  // Painel id usado pelo agendador. Vem da prop (preferencial) ou é
  // deduzido do título via slug como fallback.
  const exportPainelIdFinal = useMemo(() => {
    if (exportPainelId) return exportPainelId;
    if (!titulo) return null;
    return String(titulo)
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      || null;
  }, [exportPainelId, titulo]);

  const exportSnapshotPayload = useMemo(
    () => ({ titulo, resumo: exportResumo, secoes: exportSecoes, visual: exportVisual }),
    [titulo, exportResumo, exportSecoes, exportVisual]
  );
  useExportSnapshot(exportPainelIdFinal, exportSnapshotPayload, wrapperRef);

  // Mapeia as colunas para o formato esperado pelo ColumnResizeProvider.
  // No fluxo, garante largura DEFAULT mínima por tipo (mockup): status 132px
  // (badge "Vencido" com dot nunca quebra) e datas 118px ("02/09/2026"
  // inteiro em 13px). Só o default — o resize manual do usuário continua
  // mandando por cima.
  const resizeColumns = useMemo(
    () =>
      colunasVisiveis.map((c) => {
        let defaultWidth = c.width || null;
        if (naFluxo && defaultWidth) {
          const px = parseInt(defaultWidth, 10);
          if (!Number.isNaN(px)) {
            if (c.key === "status" && px < 132) defaultWidth = "132px";
            else if (c.tipo === "data" && px < 118) defaultWidth = "118px";
          }
        }
        return { key: c.key, defaultWidth };
      }),
    [colunasVisiveis, naFluxo]
  );
  const resizeTableId = exportPainelIdFinal || "painel-tabela-anonimo";

  const shouldRenderFilterPanel = mostrarPainelFiltros && (
    mostrarFiltroStatus ||
    Boolean(clientKey) ||
    Boolean(ambienteKey) ||
    Boolean(loteKey) ||
    Boolean(responsavelKey) ||
    Boolean(dateColumns.length) ||
    Boolean(moedaColumn)
  );

  // KPI "Total de ambientes" — barra bicolor no-prazo/vencido (mockup).
  // Só faz sentido quando a etapa tem coluna de status.
  const totalAmbientes = dadosFiltrados.length;
  const totalNoPrazo = Math.max(0, totalAmbientes - totalAtraso);
  const temColunaStatus = colunasVisiveis.some((col) => col.key === "status");
  const pctNoPrazo = totalAmbientes ? Math.round((totalNoPrazo / totalAmbientes) * 100) : 0;
  const pctVencido = totalAmbientes ? Math.round((totalAtraso / totalAmbientes) * 100) : 0;

  return (
    <ColumnResizeProvider
      tableId={resizeTableId}
      columns={resizeColumns}
      persistirNoBackend={persistirNoBackend}
    >
    <Wrapper ref={wrapperRef} $fluxo={naFluxo}>
      <TopBar>
        <NavegacaoEtapa>
          <Titulo>{titulo}</Titulo>
        </NavegacaoEtapa>
        <TopBarActions>
          {mostrarBusca &&
            (naFluxo ? (
              <SearchBox>
                <FiSearch size={15} aria-hidden="true" />
                <input
                  type="search"
                  aria-label={`Busca geral em ${titulo}`}
                  placeholder="Buscar"
                  value={filtros.busca}
                  onChange={(event) => handleFiltroChange("busca", event.target.value)}
                />
              </SearchBox>
            ) : (
              <BuscaInput
                type="search"
                aria-label={`Busca geral em ${titulo}`}
                placeholder={buscaPlaceholder}
                value={filtros.busca}
                onChange={(event) => handleFiltroChange("busca", event.target.value)}
              />
            ))}
          {!mostrarPainelFiltros && mostrarFiltroStatus && (
            <FiltroSelect
              value={filtros.status}
              onChange={(event) => handleFiltroChange("status", event.target.value)}
            >
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </FiltroSelect>
          )}
          {filtrosExtras}
          {naFluxo && temColunaStatus && (
            <SoVencidosBtn
              type="button"
              $ativo={soVencidos}
              title="Só vencidos"
              aria-label="Só vencidos"
              aria-pressed={soVencidos}
              onClick={() => {
                setPagina(1);
                setSoVencidos((v) => !v);
              }}
            >
              <FiAlertTriangle size={15} aria-hidden="true" />
              <span className="rotulo">Só vencidos</span>
            </SoVencidosBtn>
          )}
          {!naFluxo && shouldRenderFilterPanel && (
            <FilterPanel
              compact
              fluxo={naFluxo}
              title={`Filtros de ${titulo}`}
              meta={`${dadosFiltrados.length} de ${linhas.length} registros visiveis`}
              open={filtrosAbertos}
              onToggle={() => setFiltrosAbertos((current) => !current)}
              onClose={() => setFiltrosAbertos(false)}
              onReset={resetFiltros}
              chips={activeChips}
            >
              <FilterGrid>
                {mostrarFiltroStatus && (
                  <FilterField>
                    <FilterLabel>Status</FilterLabel>
                    <FiltroSelect
                      value={filtros.status}
                      onChange={(event) => handleFiltroChange("status", event.target.value)}
                    >
                      {statusOptions.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </FiltroSelect>
                  </FilterField>
                )}

                {clientKey && (
                  <FilterField>
                    <FilterLabel>Cliente</FilterLabel>
                    <FiltroSelect
                      value={filtros.cliente}
                      onChange={(event) => handleFiltroChange("cliente", event.target.value)}
                    >
                      {clientOptions.map((option) => (
                        <option key={option} value={option}>
                          {option === "TODOS" ? "Todos" : option}
                        </option>
                      ))}
                    </FiltroSelect>
                  </FilterField>
                )}

                {ambienteKey && (
                  <FilterField>
                    <FilterLabel>Ambiente</FilterLabel>
                    <BuscaInput
                      value={filtros.ambiente}
                      onChange={(event) => handleFiltroChange("ambiente", event.target.value)}
                      placeholder="Filtrar por ambiente"
                    />
                  </FilterField>
                )}

                {loteKey && (
                  <FilterField>
                    <FilterLabel>{filterMeta.loteLabel}</FilterLabel>
                    <BuscaInput
                      value={filtros.lote}
                      onChange={(event) => handleFiltroChange("lote", event.target.value)}
                      placeholder="Filtrar por lote"
                    />
                  </FilterField>
                )}

                {responsavelKey && (
                  <FilterField>
                    <FilterLabel>{filterMeta.responsavelLabel}</FilterLabel>
                    <FiltroSelect
                      value={filtros.responsavel}
                      onChange={(event) => handleFiltroChange("responsavel", event.target.value)}
                    >
                      {responsavelOptions.map((option) => (
                        <option key={option} value={option}>
                          {option === "TODOS" ? "Todos" : option}
                        </option>
                      ))}
                    </FiltroSelect>
                  </FilterField>
                )}

                {dateColumns.length > 0 && (
                  <FilterField>
                    <FilterLabel>Faixa de datas</FilterLabel>
                    <FilterInlineGroup>
                      <FiltroSelect
                        value={filtros.campoData}
                        onChange={(event) => handleFiltroChange("campoData", event.target.value)}
                      >
                        <option value="TODOS">Todas as datas</option>
                        {dateColumns.map((column) => (
                          <option key={column.key} value={column.key}>
                            {column.label}
                          </option>
                        ))}
                      </FiltroSelect>
                      <div />
                    </FilterInlineGroup>
                    <FilterInlineGroup>
                      <BuscaInput
                        type="date"
                        value={filtros.dataDe}
                        onChange={(event) => handleFiltroChange("dataDe", event.target.value)}
                      />
                      <BuscaInput
                        type="date"
                        value={filtros.dataAte}
                        onChange={(event) => handleFiltroChange("dataAte", event.target.value)}
                      />
                    </FilterInlineGroup>
                  </FilterField>
                )}

                {moedaColumn && (
                  <FilterField>
                    <FilterLabel>{moedaColumn.label}</FilterLabel>
                    <FilterInlineGroup>
                      <BuscaInput
                        type="number"
                        min="0"
                        value={filtros.valorMin}
                        onChange={(event) => handleFiltroChange("valorMin", event.target.value)}
                        placeholder="Min"
                      />
                      <BuscaInput
                        type="number"
                        min="0"
                        value={filtros.valorMax}
                        onChange={(event) => handleFiltroChange("valorMax", event.target.value)}
                        placeholder="Max"
                      />
                    </FilterInlineGroup>
                  </FilterField>
                )}
              </FilterGrid>
            </FilterPanel>
          )}
          <SoDesktop>
            <ResetColumnsButton />
          </SoDesktop>
          <ExportCompacto>
            <BotaoExportacao
              titulo={titulo}
              resumo={exportResumo}
              secoes={exportSecoes}
              visual={exportVisual}
              filtrosAtivos={activeChips}
            />
          </ExportCompacto>
        </TopBarActions>
      </TopBar>

      {conteudoAcimaTotalizadores}

      {mostrarTotalizadores && (
      <Totalizadores data-pdf="totalizadores">
        <TotalCard $fluxo={naFluxo}>
          <TotalTopo $fluxo={naFluxo}>
            <TotalLabel $fluxo={naFluxo}>{labelContagem}</TotalLabel>
            <TotalValor $fluxo={naFluxo}>{totalAmbientes}</TotalValor>
          </TotalTopo>
          {naFluxo && temColunaStatus && (
            <>
              <TotalBar>
                <TotalBarOk
                  initial={{ width: "0%" }}
                  animate={{ width: `${pctNoPrazo}%` }}
                  exit={{ width: "0%", transition: { duration: 0.3, ease: "easeIn" } }}
                  transition={{ duration: 0.9, ease: [0.22, 0.61, 0.36, 1] }}
                />
                <TotalBarVenc
                  initial={{ width: "0%" }}
                  animate={{ width: `${pctVencido}%` }}
                  exit={{ width: "0%", transition: { duration: 0.3, ease: "easeIn" } }}
                  transition={{ duration: 0.9, ease: [0.22, 0.61, 0.36, 1] }}
                />
              </TotalBar>
              <TotalLegend>
                <span className="ok">{totalNoPrazo} no prazo</span>
                <span className="venc">{totalAtraso} vencidos</span>
              </TotalLegend>
            </>
          )}
        </TotalCard>
        <TotalCard $fluxo={naFluxo} $tone={totalAtraso > 0 ? "danger" : "default"}>
          <TotalTopo $fluxo={naFluxo}>
            <TotalLabel $fluxo={naFluxo} $tone={totalAtraso > 0 ? "danger" : "default"}>
              Em atraso
            </TotalLabel>
            <TotalValor $fluxo={naFluxo} $tone={totalAtraso > 0 ? "danger" : "default"}>
              {totalAtraso}
            </TotalValor>
          </TotalTopo>
          {naFluxo && <TotalSub>de {totalAmbientes} ambientes</TotalSub>}
        </TotalCard>
        <TotalCard $fluxo={naFluxo}>
          <TotalTopo $fluxo={naFluxo}>
            <TotalLabel $fluxo={naFluxo}>Valor total</TotalLabel>
            <TotalValor $fluxo={naFluxo}>
              {precosVisiveis ? formatarMoeda(totalValor) : "••••••"}
            </TotalValor>
          </TotalTopo>
          {naFluxo && <TotalSub>em {totalAmbientes} ambientes</TotalSub>}
        </TotalCard>
        {totalizadores
          ?.filter((t) => {
            // Evita duplicar os 3 cards padrão. Páginas que passam
            // "Total de ambientes" ou "Em atraso" como totalizador
            // extra são ignoradas (já estão renderizados acima).
            const label = String(t?.label || "").trim().toLowerCase();
            if (label === labelContagem.toLowerCase()) return false;
            if (label === "total de ambientes") return false;
            if (label === "em atraso") return false;
            if (label === "valor total") return false;
            return true;
          })
          .map((totalizador, index) => (
            <TotalCard key={`${totalizador.label}-${index}`} $fluxo={naFluxo}>
              <TotalTopo $fluxo={naFluxo}>
                <TotalLabel $fluxo={naFluxo}>{totalizador.label}</TotalLabel>
                <TotalValor $fluxo={naFluxo}>{totalizador.valor}</TotalValor>
              </TotalTopo>
              {totalizador.sub && <TotalSub>{totalizador.sub}</TotalSub>}
            </TotalCard>
          ))}
      </Totalizadores>
      )}

      {/* Celular/tablet: cards compactos. Em modo export sempre tabela
          (consistencia entre dispositivos). */}
      {usarCards ? (
        <CardListView
          colunas={colunasVisiveis}
          linhas={dadosPagina}
          renderCellValue={renderCellValue}
          onRowClick={rowClickEfetivo}
          fluxo={naFluxo}
          precosVisiveis={precosVisiveis}
          tomDaLinha={(row) => row.statusVariant ?? inferStatusVariant(row.status)}
        />
      ) : (
      <TabelaWrapper data-pdf="tabela" data-export-expand>
        <Tabela $density={densidadeEfetiva}>
          <ResizableColgroup />
          <thead>
            <tr>
              {colunasVisiveis.map((column) => (
                <ResizableTh
                  as={Th}
                  columnKey={column.key}
                  key={column.key}
                  $density={densidadeEfetiva}
                  onClick={() => handleOrdenar(column.key)}
                  data-no-wrap={column.noWrap ? "true" : undefined}
                >
                  {/* Os indicadores de ordenar/filtrar sao affordances de
                      tela: saem da captura via data-export-no-print (regra em
                      styles/GlobalStyles.jsx). Em modo export a regra
                      `opacity: 1 !important` ainda por cima ANULAVA a
                      transparencia deles (ThIconHint 0.3, ThFunil 0.55), entao
                      apareciam mais fortes na imagem do que na tela. */}
                  <ThInner>
                    {column.label}
                    {ordenacao.column === column.key ? (
                      ordenacao.direction === "asc" ? (
                        <FiChevronUp size={13} data-export-no-print />
                      ) : (
                        <FiChevronDown size={13} data-export-no-print />
                      )
                    ) : (
                      <ThIconHint data-export-no-print>
                        <FiChevronDown size={13} />
                      </ThIconHint>
                    )}
                    {naFluxo && (
                      <ThFunil
                        type="button"
                        data-funil-btn
                        data-export-no-print
                        $ativo={colFiltros[column.key] !== undefined}
                        onClick={(event) => abrirFunil(column.key, event)}
                        title="Filtrar coluna"
                        aria-label={`Filtrar por ${column.label}`}
                      >
                        <FiFilter size={12} />
                      </ThFunil>
                    )}
                  </ThInner>
                </ResizableTh>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* `mode="popLayout"` permite que as linhas antigas saiam (fade)
                ao mesmo tempo que as novas entram, mas tirando as antigas do
                fluxo de layout — evita duplicação de altura do tbody e o
                "stutter" do `mode="wait"`. A key inclui `paginaAtual` para
                garantir que linhas com mesmo cliente em paginas diferentes
                sejam tratadas como elementos distintos. */}
            <AnimatePresence mode="popLayout" initial={false}>
              {dadosPagina.length === 0 ? (
                <tr key="empty">
                  <EmptyTd colSpan={colunasVisiveis.length}>Nenhum registro encontrado</EmptyTd>
                </tr>
              ) : (
                dadosPagina.map((row, index) => {
                  // Tom da linha p/ a barra de status à esquerda (só no fluxo).
                  const tomLinha = naFluxo
                    ? row.statusVariant ?? inferStatusVariant(row.status)
                    : "default";
                  return (
                  <TrLinha
                    key={`p${paginaAtual}-${row.cliente ?? row.id ?? row.ambiente ?? "linha"}-${index}`}
                    // Durante exportação (renderAll), pula animações para
                    // que linhas fiquem visíveis imediatamente — html2canvas
                    // capturaria opacity:0 caso contrário. Listas grandes
                    // (> MAX_LINHAS_ANIMADAS) também não animam — ver
                    // comentário da constante.
                    initial={
                      renderAll || dadosPagina.length > MAX_LINHAS_ANIMADAS
                        ? false
                        : { opacity: 0, y: 4 }
                    }
                    animate={{ opacity: 1, y: 0 }}
                    exit={
                      dadosPagina.length > MAX_LINHAS_ANIMADAS
                        ? undefined
                        : { opacity: 0, transition: { duration: 0.08 } }
                    }
                    transition={
                      renderAll || dadosPagina.length > MAX_LINHAS_ANIMADAS
                        ? { duration: 0 }
                        : { delay: index * 0.012, duration: 0.16 }
                    }
                    onClick={rowClickEfetivo ? () => rowClickEfetivo(row) : undefined}
                    $fluxo={naFluxo}
                    $clicavel={Boolean(rowClickEfetivo)}
                    // Tint da linha inteira pela cor do estado (mockup),
                    // só no fluxo e quando há estado. Vive no styled (não em
                    // style inline) para o :hover conseguir sobrepor. Passa
                    // o NOME do tom — o styled resolve var(--{tom}-row).
                    $tint={
                      naFluxo && tomLinha !== "default" ? tomLinha : undefined
                    }
                  >
                    {colunasVisiveis.map((column, ci) => (
                      <Td
                        key={column.key}
                        $density={densidadeEfetiva}
                        $isStatus={column.key === "status"}
                        $barra={ci === 0 ? tomLinha : "default"}
                        data-no-wrap={column.noWrap ? "true" : undefined}
                        title={tituloCelula(row, column)}
                      >
                        {renderCellValue(row, column)}
                      </Td>
                    ))}
                  </TrLinha>
                  );
                })
              )}
            </AnimatePresence>
          </tbody>
        </Tabela>
      </TabelaWrapper>
      )}

      {mostrarPaginacao && totalPaginas > 1 && !renderAll && (
        <Paginacao data-export-no-print>
          <PagInfo>
            {inicio + 1}–{Math.min(inicio + ITENS_POR_PAGINA, dadosFiltrados.length)} de{" "}
            {dadosFiltrados.length}
          </PagInfo>
          <PagBotoes>
            <PagBtn
              onClick={() => setPagina((current) => Math.max(1, current - 1))}
              disabled={paginaAtual === 1}
            >
              ‹ Anterior
            </PagBtn>
            {Array.from({ length: totalPaginas }, (_, index) => index + 1)
              .filter(
                (pageNumber) =>
                  pageNumber === 1 ||
                  pageNumber === totalPaginas ||
                  Math.abs(pageNumber - paginaAtual) <= 2
              )
              .reduce((pages, pageNumber, index, allPages) => {
                if (index > 0 && pageNumber - allPages[index - 1] > 1) {
                  pages.push("...");
                }

                pages.push(pageNumber);
                return pages;
              }, [])
              .map((pageNumber, index) =>
                pageNumber === "..." ? (
                  <PagEllipsis key={`ellipsis-${index}`}>…</PagEllipsis>
                ) : (
                  <PagBtn
                    key={pageNumber}
                    $active={pageNumber === paginaAtual}
                    onClick={() => setPagina(pageNumber)}
                  >
                    {pageNumber}
                  </PagBtn>
                )
              )}
            <PagBtn
              onClick={() => setPagina((current) => Math.min(totalPaginas, current + 1))}
              disabled={paginaAtual === totalPaginas}
            >
              Próximo ›
            </PagBtn>
          </PagBotoes>
        </Paginacao>
      )}

      {funil &&
        (() => {
          const column = colunasVisiveis.find((c) => c.key === funil.col);
          const cur = colFiltros[funil.col];
          const q = (funilBusca || "").toLowerCase();
          const valores = distinctValores(funil.col).filter((v) =>
            v.toLowerCase().includes(q)
          );
          return (
            <FunilPopover
              data-funil-popover
              style={{ top: funil.top, left: funil.left }}
            >
              <FunilHead>
                <span>{column?.label}</span>
                <FunilLimpar
                  type="button"
                  onClick={() => limparColFiltro(funil.col)}
                >
                  Limpar
                </FunilLimpar>
              </FunilHead>
              <FunilBusca>
                <FiSearch size={13} aria-hidden="true" />
                <input
                  value={funilBusca}
                  onChange={(event) => setFunilBusca(event.target.value)}
                  placeholder="Buscar valor"
                  aria-label="Buscar valor da coluna"
                />
              </FunilBusca>
              <FunilLista>
                <FunilItem
                  as="label"
                  onClick={(event) => {
                    event.preventDefault();
                    toggleTodosFunil(funil.col);
                  }}
                >
                  <input type="checkbox" readOnly checked={cur === undefined} />
                  <span>(Selecionar todos)</span>
                </FunilItem>
                {valores.map((v) => {
                  const marcado = cur === undefined ? true : cur.includes(v);
                  return (
                    <FunilItem
                      key={v}
                      as="label"
                      onClick={(event) => {
                        event.preventDefault();
                        toggleFunilVal(funil.col, v);
                      }}
                    >
                      <input type="checkbox" readOnly checked={marcado} />
                      <span>{v}</span>
                    </FunilItem>
                  );
                })}
                {valores.length === 0 && (
                  <FunilVazio>Nenhum valor</FunilVazio>
                )}
              </FunilLista>
            </FunilPopover>
          );
        })()}

      {/* Drawer de detalhe do ambiente (fluxo) — dados + linha do tempo. */}
      <AmbienteDetalhesDrawer
        ambiente={ambienteDrawer}
        onClose={() => setAmbienteDrawer(null)}
      />
    </Wrapper>
    </ColumnResizeProvider>
  );
}

